function addDragScroller(element) {
    if (!element) return () => {};

    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    const resetDrag = () => {
      isDragging = false;
      element.style.cursor = 'grab';
      element.style.userSelect = '';
    };

    const handleMouseDown = (e) => {
      isDragging = true;
      startX = e.pageX - element.offsetLeft;
      scrollLeft = element.scrollLeft;
      element.style.cursor = 'grabbing';
      element.style.userSelect = 'none';
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - element.offsetLeft;
      element.scrollLeft = scrollLeft - (x - startX);
    };

    element.style.cursor = 'grab';
    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseup', resetDrag);
    element.addEventListener('mouseleave', resetDrag);

    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseup', resetDrag);
      element.removeEventListener('mouseleave', resetDrag);
    };
}

// ── Shared stylesheets (created once, reused across all instances) ───────────

const tagSheet = new CSSStyleSheet();
tagSheet.replaceSync(`
  :host {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    font-family: var(--text-font-family);
    font-style: normal;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    background-color: var(--off-white);
    vertical-align: middle;
    text-wrap: nowrap;
    color: hsl(from var(--space-color, #44A1FF) h s calc(l * .333));
  }
  svg path {
    fill: hsl(from var(--space-color, #44A1FF) h s calc(l * .333));
  }
`);

const ARROW_SVG = `<svg fill="none" height="20" viewBox="0 0 21 20" width="21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g stroke="#02090a" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><path d="m1.5 10h18"/><path d="m11.0996 18.4 8.4-8.40002-8.4-8.4"/></g></svg>`;

const LOCATION_SVG = `<svg fill="none" height="13" viewBox="0 0 10 13" width="10" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path clip-rule="evenodd" d="m8.99363 7.36154c.31121-.63352.50637-1.48718.50637-2.04692 0-2.76142-2.12665-4.766768-4.75-4.766768s-4.75 2.006638-4.75 4.768058c0 .55974.195163 1.41211.506371 2.04563.320467.65237.763989 1.31862 1.244169 1.94677.82059 1.07349 1.74824 2.03569 2.35178 2.62719.36309.3558.93227.3558 1.29536 0 .60354-.5915 1.53119-1.5537 2.35178-2.62719.48018-.62815.9237-1.2944 1.24417-1.94677zm-4.24363-.31369c1.10457 0 2-.89543 2-2s-.89543-2-2-2-2 .89543-2 2 .89543 2 2 2z" fill-rule="evenodd"/></svg>`;

// ── Media card stylesheets ───────────────────────────────────────────────────

const buttonSheet = new CSSStyleSheet();
buttonSheet.replaceSync(`
  :host {
    width: 100%;
  }
  a {
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px;
    gap: 16px;
    text-decoration: none;
    font-family: var(--text-font-family);
    font-size: 20px;
    line-height: 20px;
    color: black;
    background-color: var(--space-color, #44A1FF);
    width: 100%;
  }
  a .arrow {
    width: 0;
    opacity: 0;
    height: 20px;
    overflow: hidden;
    transition: width .25s, opacity .5s;
    position: relative;
  }
  a .arrow svg {
    position: absolute;
    right: 0;
  }
  a:hover .arrow {
    width: 21px;
    opacity: 1;
  }
`);

const mediaCardSheet = new CSSStyleSheet();
mediaCardSheet.replaceSync(`
  :host {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 24px 16px;
    border-bottom: 1px solid var(--color-border);
  }
  h3 {
    font-style: normal;
    margin: 0;
    font-family: var(--title-font-family);
    font-size: 30px;
    line-height: 34px;
    font-weight: 400;
  }
  h4 {
    text-transform: uppercase;
    margin: 0;
    color: var(--color-primary);
    letter-spacing: -0.24px;
    font-family: var(--text-font-family);
    font-size: 20px;
    line-height: 24px;
    font-weight: 700;
  }
  .media {
    width: 100%;
    overflow-x: scroll;
    display: flex;
    gap: 8px;
    scrollbar-width: none;
    & img {
      aspect-ratio: 1/1;
      width: 160px;
      height: auto;
      object-fit: cover;
      pointer-events: none;
    }
  }
  ul {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: row;
    gap: 8px;
    & li {
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }
  br {
    display: none;
  }
  @media screen and (min-width: 726px) {
    :host {
      display: grid;
      grid-template-columns: 135px 1fr 187px;
      grid-template-areas:
        "date title cta"
        "date tags cta"
        "date media media";
      border-bottom: none;
      padding: 24px 0;
    }
    h4 {
      grid-area: date;
    }
    h3 {
      grid-area: title;
    }
    ul {
      grid-area: tags;
    }
    spaces-button {
      grid-area: cta;
    }
    .media {
      grid-area: media;
      gap: 16px;
    }
    .media img {
      width: 260px;
      height: auto;
    }
    br {
      display: initial;
    }
  }
  @media screen and (min-width: 1024px) {
    :host {
      grid-template-columns: 135px 1fr 206px;
    }
  }

  /* ── Skeleton loading state ── */

  @keyframes shimmer {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  .skeleton {
    background: var(--light-gray, #e5e5e5);
    animation: shimmer 1.5s ease-in-out infinite;
    border-radius: 4px;
  }
  .skeleton-date {
    height: 24px;
    width: 120px;
  }
  .skeleton-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
  }
  .skeleton-title-line {
    height: 34px;
  }
  .skeleton-tags {
    display: flex;
    gap: 8px;
  }
  .skeleton-tag {
    height: 22px;
    width: 90px;
    border-radius: 2px;
  }
  .skeleton-photos {
    display: flex;
    gap: 8px;
    overflow: hidden;
    width: 100%;
  }
  .skeleton-photo {
    width: 160px;
    min-width: 160px;
    aspect-ratio: 1/1;
    border-radius: 0;
  }
  .skeleton-button {
    height: 68px;
    width: 100%;
    border-radius: 0;
  }
  @media screen and (min-width: 726px) {
    .skeleton-date { grid-area: date; }
    .skeleton-title { grid-area: title; }
    .skeleton-tags { grid-area: tags; align-self: center; }
    .skeleton-photos {
      grid-area: media;
      gap: 16px;
    }
    .skeleton-photos .skeleton-photo {
      width: 260px;
      min-width: 260px;
    }
    .skeleton-button { grid-area: cta; }
  }
`);

// ── Component definition ──────────────────────────────────────────────────────

if (!customElements.get('spaces-event-media-card')) {
  customElements.define('spaces-event-media-card', class extends HTMLElement {
  
    cleanup = null;
  
    connectedCallback() {
      this.render();
      const mediaElement = this.shadowRoot?.querySelector('.media');
      if (mediaElement) {
        this.cleanup = addDragScroller(mediaElement);
      }
    }

    disconnectedCallback() {
      if (this.cleanup) {
        this.cleanup();
        this.cleanup = null;
      }
    }

    render() {
      const shadowRoot = this.shadowRoot || this.attachShadow({ mode: 'open' });
      shadowRoot.adoptedStyleSheets = [mediaCardSheet];

      const title = this.getAttribute('title');
      if (!title) {
        shadowRoot.innerHTML = `
          <div class="skeleton skeleton-date"></div>
          <div class="skeleton-title">
            <div class="skeleton skeleton-title-line" style="width: 80%"></div>
            <div class="skeleton skeleton-title-line" style="width: 50%"></div>
          </div>
          <div class="skeleton-tags">
            <div class="skeleton skeleton-tag"></div>
            <div class="skeleton skeleton-tag"></div>
          </div>
          <div class="skeleton-photos">
            <div class="skeleton skeleton-photo"></div>
            <div class="skeleton skeleton-photo"></div>
            <div class="skeleton skeleton-photo"></div>
            <div class="skeleton skeleton-photo"></div>
          </div>
          <div class="skeleton skeleton-button"></div>
        `;
        return;
      }

      const rsvpLink = this.getAttribute('rsvp-link') || '#';
      const startDate = this.getAttribute('start-date');
      const endDate = this.getAttribute('end-date');

      const type = this.getAttribute('type') || 'Community';
      const space = {
        label: this.getAttribute('space-label') || '',
        color: this.getAttribute('space-color') || '#44A1FF',
      };

      // Set CSS custom property for dynamic color — inherited by child shadow DOMs
      this.style.setProperty('--space-color', space.color);

      let featuredPhotos = [];
      try {
        featuredPhotos = JSON.parse(this.getAttribute('featured-photos') || '[]');
      } catch (e) {
        console.error('Failed to parse featured-photos:', e);
      }

      const dateRangeHtml = this.#formatDateRange(startDate, endDate);
      
      const photosHtml = featuredPhotos.map(photoUrl => 
        `<img src="${photoUrl}" width="160" height="160" loading="eager" alt="Featured photo of ${title}" draggable="false" />`
      ).join('');

      shadowRoot.innerHTML = `
        <h4>${dateRangeHtml}</h4>
        <h3>${title}</h3>
        <ul>
          <li><spaces-tag glyph="location">${space.label}</spaces-tag></li>
          <li><spaces-tag>${type}</spaces-tag></li>
        </ul>
        <div class="media">
          ${photosHtml}
        </div>
        <spaces-button href="${rsvpLink}" target="_blank">Learn more</spaces-button>
      `;

      shadowRoot.querySelectorAll('spaces-tag').forEach(tag => {
        const glyph = tag.getAttribute('glyph');
        const shadow = tag.attachShadow({ mode: 'open' });
        shadow.adoptedStyleSheets = [tagSheet];
        shadow.innerHTML = (glyph === 'location' ? LOCATION_SVG : '') + '<slot></slot>';
      });

      shadowRoot.querySelectorAll('spaces-button').forEach(button => {
        const href = button.getAttribute('href') || '#';
        const target = button.getAttribute('target');
        const shadow = button.attachShadow({ mode: 'open' });
        shadow.adoptedStyleSheets = [buttonSheet];
        shadow.innerHTML = `
          <a href="${href}"${target ? ` target="${target}" rel="noopener noreferrer"` : ''}>
            <slot></slot>
            <span class="arrow">${ARROW_SVG}</span>
          </a>`;
      });
    }

    #formatDateRange(startDateStr, endDateStr) {
      if (!startDateStr) return '';
      const start = new Date(startDateStr);
      const end = endDateStr ? new Date(endDateStr) : start;

      // Display the stored dates in US Eastern Time (ET) without converting to
      // the viewer's local timezone. Route every part through the same timeZone so
      // the month name, day, and year always agree.
      const TZ = 'America/New_York';
      const startMonth = start.toLocaleDateString('en-US', { month: 'short', timeZone: TZ }).toUpperCase();
      const endMonth = end.toLocaleDateString('en-US', { month: 'short', timeZone: TZ }).toUpperCase();
      const startDay = Number(start.toLocaleDateString('en-US', { day: 'numeric', timeZone: TZ }));
      const endDay = Number(end.toLocaleDateString('en-US', { day: 'numeric', timeZone: TZ }));
      const startMonthIdx = Number(start.toLocaleDateString('en-US', { month: 'numeric', timeZone: TZ }));
      const endMonthIdx = Number(end.toLocaleDateString('en-US', { month: 'numeric', timeZone: TZ }));
      const startYear = Number(start.toLocaleDateString('en-US', { year: 'numeric', timeZone: TZ }));
      const endYear = Number(end.toLocaleDateString('en-US', { year: 'numeric', timeZone: TZ }));

      const sameDay = startYear === endYear && startMonthIdx === endMonthIdx && startDay === endDay;
      const sameMonth = startMonthIdx === endMonthIdx && startYear === endYear;
      const sameYear = startYear === endYear;

      if (sameDay) {
        return `${startMonth}&nbsp;${startDay},<br/> ${startYear}`;
      } else if (sameYear) {
        if (sameMonth) {
          return `${startMonth}&nbsp;${startDay} – ${endDay},<br/> ${startYear}`;
        } else {
          return `${startMonth}&nbsp;${startDay} – ${endMonth}&nbsp;${endDay},<br/> ${startYear}`;
        }
      } else {
        return `${startMonth}&nbsp;${startDay}, ${startYear}<br/> – ${endMonth}&nbsp;${endDay}, ${endYear}`;
      }
    }

  });
}
